/**
 * `/macro/relationships` and `/macro/relationships/pair` — the nightly derive's
 * correlations for the Relationships section (`docs/plans/macro.md`).
 *
 * Server: `services/qv-data/app/routers/macro_derived.py` and
 * `app/macro_derive/read.py` (branch `macro/derive`). **Admin only** on the
 * server too, so a non-admin gets a 403 here — the section host refuses before
 * this is ever called. 503 means the `macro` artifact is not on that machine
 * yet, and is reported as such rather than as a failure.
 *
 * Shapes are columnar (one list per column), mapped by name: a field the
 * server renames fails here, in a type, rather than going quietly missing.
 * Reads throw — the section says what went wrong; there is nothing to retry
 * that the next open will not.
 */

import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

/** `p` price (weekly change = 100·ln ratio, %), `r` rate (pp), `l` level (points). */
export type AssetKind = "p" | "r" | "l";

export interface MacroAsset {
  key: string;
  name: string;
  kind: AssetKind;
  /** What the derive reads for it (`SPY`, `us.treasury.yield_10_year`). */
  proxy: string;
}

/** One window's matrix: pair `a` before `b` in the matrix order. */
export interface MatrixColumns {
  a: string[];
  b: string[];
  r: Array<number | null>;
  beta: Array<number | null>;
  betaRev: Array<number | null>;
}

export interface HedgeGauge {
  a: string;
  b: string;
  r13: number | null;
  r52: number | null;
  /** The server's verdict at its own warning line; the section recomputes it at the viewer's. */
  warn: boolean | null;
  week: string[];
  r13Series: Array<number | null>;
  r52Series: Array<number | null>;
}

export interface RelationshipsPayload {
  builtAt: string | null;
  asOf: string | null;
  absent: string[];
  assets: MacroAsset[];
  /** In weeks: 13, 52, 156. */
  windows: number[];
  breakThreshold: number;
  hedgeWarn: number;
  /** Keyed by window in weeks, as a string (`"13"`, `"52"`, `"156"`). */
  matrix: Record<string, MatrixColumns>;
  hedges: HedgeGauge[];
}

export interface PairSeries {
  week: string[];
  r: Array<number | null>;
  beta: Array<number | null>;
  betaRev: Array<number | null>;
}

export interface PairPayload {
  a: string;
  b: string;
  assets: MacroAsset[];
  /** Weekly changes: x is `a`, y is `b`. */
  points: { week: string[]; x: Array<number | null>; y: Array<number | null> };
  /** Rolling r / β per window, keyed as `matrix`. */
  series: Record<string, PairSeries>;
}

/** The artifact has not been pulled to this machine yet (503). */
export class MacroNotReady extends Error {}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
const nums = (v: unknown): Array<number | null> => (Array.isArray(v) ? v.map(num) : []);
const kind = (v: unknown): AssetKind => (v === "r" || v === "l" ? v : "p");

function mapAssets(v: unknown): MacroAsset[] {
  return (Array.isArray(v) ? v : []).map((a: any) => ({
    key: String(a?.key ?? ""),
    name: String(a?.name ?? a?.key ?? ""),
    kind: kind(a?.kind),
    proxy: String(a?.proxy ?? ""),
  }));
}

export function mapRelationships(j: any): RelationshipsPayload {
  const matrix: Record<string, MatrixColumns> = {};
  for (const [w, m] of Object.entries((j?.matrix ?? {}) as Record<string, any>)) {
    matrix[w] = { a: strs(m?.a), b: strs(m?.b), r: nums(m?.r), beta: nums(m?.beta), betaRev: nums(m?.betaRev) };
  }
  return {
    builtAt: typeof j?.builtAt === "string" ? j.builtAt : null,
    asOf: typeof j?.asOf === "string" ? j.asOf : null,
    absent: strs(j?.absent),
    assets: mapAssets(j?.assets),
    windows: nums(j?.windows).filter((n): n is number => n != null),
    breakThreshold: num(j?.breakThreshold) ?? 0.4,
    hedgeWarn: num(j?.hedgeWarn) ?? 0,
    matrix,
    hedges: (Array.isArray(j?.hedges) ? j.hedges : []).map((h: any): HedgeGauge => ({
      a: String(h?.a ?? ""),
      b: String(h?.b ?? ""),
      r13: num(h?.r13),
      r52: num(h?.r52),
      warn: typeof h?.warn === "boolean" ? h.warn : null,
      week: strs(h?.week),
      r13Series: nums(h?.r13Series),
      r52Series: nums(h?.r52Series),
    })),
  };
}

export function mapPair(j: any): PairPayload {
  const series: Record<string, PairSeries> = {};
  for (const [w, s] of Object.entries((j?.series ?? {}) as Record<string, any>)) {
    series[w] = { week: strs(s?.week), r: nums(s?.r), beta: nums(s?.beta), betaRev: nums(s?.betaRev) };
  }
  const p = j?.points ?? {};
  return {
    a: String(j?.a ?? ""),
    b: String(j?.b ?? ""),
    assets: mapAssets(j?.assets),
    points: { week: strs(p.week), x: nums(p.x), y: nums(p.y) },
    series,
  };
}

async function get(path: string): Promise<any> {
  const res = await fetch(`${BASE}${path}`, { headers: await requestHeaders() });
  if (res.status === 503) throw new MacroNotReady("The relationships data has not been built on this server yet.");
  if (!res.ok) {
    const detail = await res.json().then((b) => b?.detail).catch(() => undefined);
    throw new Error(typeof detail === "string" ? detail : `Relationships: ${res.status}`);
  }
  return res.json();
}

export async function fetchRelationships(): Promise<RelationshipsPayload> {
  return mapRelationships(await get("/macro/relationships"));
}

export async function fetchRelationshipPair(a: string, b: string): Promise<PairPayload> {
  const q = new URLSearchParams({ a, b });
  return mapPair(await get(`/macro/relationships/pair?${q}`));
}
